import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface AssessmentData {
  [sectionId: string]: {
    [questionId: string]: string;
  };
}

interface RecommendationItem {
  title: string;
  description: string;
  priority: 'high' | 'medium' | 'low';
  category: string;
  actionItems: string[];
}

interface ContextualRecommendationsProps {
  assessmentData: AssessmentData;
  sectionScores: { [key: string]: { score: number; max: number } };
  selectedRole: string;
}

const ContextualRecommendations = ({ assessmentData, sectionScores, selectedRole }: ContextualRecommendationsProps) => {
  const generateRecommendations = (): RecommendationItem[] => {
    const recommendations: RecommendationItem[] = [];

    // Calculate section percentages
    const orgScore = Math.round((sectionScores['organizational']?.score / sectionScores['organizational']?.max) * 100);
    const techScore = Math.round((sectionScores['technical']?.score / sectionScores['technical']?.max) * 100);
    const govScore = Math.round((sectionScores['governance']?.score / sectionScores['governance']?.max) * 100);
    const talentScore = Math.round((sectionScores['talent']?.score / sectionScores['talent']?.max) * 100);

    // Organizational Readiness Recommendations
    if (orgScore < 60) {
      const strategyAnswer = assessmentData['organizational']?.['q1'];
      if (strategyAnswer === 'c' || strategyAnswer === 'd') {
        recommendations.push({
          title: 'Develop Comprehensive AI Strategy',
          description: 'Your organization lacks a formal AI strategy. This is the critical first step for successful AI adoption.',
          priority: 'high',
          category: 'Organizational Readiness',
          actionItems: [
            'Conduct executive workshop to define AI vision and objectives',
            'Map current state and identify high-impact AI use cases',
            'Create 12-18 month AI roadmap with clear milestones',
            'Establish success metrics and KPIs for AI initiatives'
          ]
        });
      }

      const sponsorshipAnswer = assessmentData['organizational']?.['q2'];
      if (sponsorshipAnswer === 'c' || sponsorshipAnswer === 'd') {
        recommendations.push({
          title: 'Secure Executive Sponsorship',
          description: 'AI transformation requires active C-suite engagement and dedicated resources.',
          priority: 'high',
          category: 'Organizational Readiness',
          actionItems: [
            'Present business case for AI to executive leadership',
            'Identify and appoint Chief AI Officer or equivalent role',
            'Allocate dedicated budget for AI initiatives',
            'Establish executive steering committee for AI governance'
          ]
        });
      }
    }

    // Technical Infrastructure Recommendations
    if (techScore < 60) {
      const dataAnswer = assessmentData['technical']?.['q4'];
      if (dataAnswer === 'c' || dataAnswer === 'd') {
        recommendations.push({
          title: 'Modernize Data Infrastructure',
          description: 'AI success depends on accessible, high-quality data. Your current infrastructure needs significant improvement.',
          priority: 'high',
          category: 'Technical Infrastructure',
          actionItems: [
            'Conduct comprehensive data audit across all systems',
            'Implement data governance framework and quality standards',
            'Consolidate data silos into unified data platform',
            'Establish data cataloging and metadata management'
          ]
        });
      }

      const apiAnswer = assessmentData['technical']?.['q5'];
      if (apiAnswer === 'c' || apiAnswer === 'd') {
        recommendations.push({
          title: 'Build Integration Architecture',
          description: 'Modern AI solutions require robust API and integration capabilities.',
          priority: 'medium',
          category: 'Technical Infrastructure',
          actionItems: [
            'Design API-first architecture for system interoperability',
            'Implement enterprise service bus or integration platform',
            'Develop API governance and security standards',
            'Create integration testing and monitoring framework'
          ]
        });
      }

      const cloudAnswer = assessmentData['technical']?.['q6'];
      if (cloudAnswer === 'c' || cloudAnswer === 'd') {
        recommendations.push({
          title: 'Accelerate Cloud Migration',
          description: 'Cloud infrastructure provides the scalability and flexibility needed for AI workloads.',
          priority: 'medium',
          category: 'Technical Infrastructure',
          actionItems: [
            'Develop cloud migration strategy and prioritization',
            'Select appropriate cloud provider(s) for AI workloads',
            'Implement hybrid cloud architecture if needed',
            'Train IT team on cloud-native technologies and practices'
          ]
        });
      }
    }

    // Governance & Compliance Recommendations
    if (govScore < 60) {
      const policyAnswer = assessmentData['governance']?.['q7'];
      if (policyAnswer === 'c' || policyAnswer === 'd') {
        recommendations.push({
          title: 'Establish AI Governance Framework',
          description: 'Formal governance policies are essential for responsible and compliant AI deployment.',
          priority: 'high',
          category: 'Governance & Compliance',
          actionItems: [
            'Form AI ethics and governance committee',
            'Develop AI usage policies and acceptable use guidelines',
            'Create AI risk assessment and approval process',
            'Implement ongoing monitoring and audit procedures'
          ]
        });
      }

      const regulatoryAnswer = assessmentData['governance']?.['q8'];
      if (regulatoryAnswer === 'c' || regulatoryAnswer === 'd') {
        recommendations.push({
          title: 'Build Regulatory Compliance Program',
          description: 'Understanding and adhering to AI regulations is critical for risk mitigation.',
          priority: 'high',
          category: 'Governance & Compliance',
          actionItems: [
            'Conduct regulatory landscape analysis for your industry',
            'Engage legal counsel specializing in AI and data privacy',
            'Develop compliance checklist and documentation requirements',
            'Establish regular compliance training for relevant staff'
          ]
        });
      }

      const securityAnswer = assessmentData['governance']?.['q9'];
      if (securityAnswer === 'c' || securityAnswer === 'd') {
        recommendations.push({
          title: 'Strengthen Security Posture',
          description: 'AI systems require enterprise-grade security to protect sensitive data and models.',
          priority: 'high',
          category: 'Governance & Compliance',
          actionItems: [
            'Conduct comprehensive security audit and penetration testing',
            'Implement zero-trust security architecture',
            'Deploy AI-specific security controls (model protection, data encryption)',
            'Establish incident response plan for AI-related security events'
          ]
        });
      }
    }

    // Talent & Skills Recommendations
    if (talentScore < 60) {
      const expertiseAnswer = assessmentData['talent']?.['q10'];
      if (expertiseAnswer === 'c' || expertiseAnswer === 'd') {
        recommendations.push({
          title: 'Build Internal AI Capability',
          description: 'Developing in-house AI expertise reduces dependency and accelerates adoption.',
          priority: 'high',
          category: 'Talent & Skills',
          actionItems: [
            'Partner with Mindshift AI for hands-on training and knowledge transfer',
            'Identify high-potential employees for AI upskilling programs',
            'Create AI center of excellence with dedicated resources',
            'Establish mentorship program pairing internal staff with AI experts'
          ]
        });
      }

      const workforceAnswer = assessmentData['talent']?.['q11'];
      if (workforceAnswer === 'c' || workforceAnswer === 'd') {
        recommendations.push({
          title: 'Launch Workforce AI Training Program',
          description: 'Preparing your workforce for AI-augmented roles is essential for successful adoption.',
          priority: 'medium',
          category: 'Talent & Skills',
          actionItems: [
            'Develop role-specific AI training curriculum',
            'Implement change management program to address AI concerns',
            'Create internal AI champions network across departments',
            'Establish continuous learning platform for AI skills development'
          ]
        });
      }

      const acquisitionAnswer = assessmentData['talent']?.['q12'];
      if (acquisitionAnswer === 'c' || acquisitionAnswer === 'd') {
        recommendations.push({
          title: 'Develop AI Talent Acquisition Strategy',
          description: 'Strategic hiring of AI talent complements internal development efforts.',
          priority: 'low',
          category: 'Talent & Skills',
          actionItems: [
            'Define key AI roles and competency requirements',
            'Develop competitive compensation and benefits package',
            'Build employer brand highlighting AI innovation culture',
            'Consider partnerships with universities for talent pipeline'
          ]
        });
      }
    }

    // Role-specific recommendations
    if (selectedRole === 'executive') {
      recommendations.push({
        title: 'Executive AI Leadership Program',
        description: 'As an executive leader, understanding AI strategy and governance is crucial for guiding your organization.',
        priority: 'medium',
        category: 'Leadership Development',
        actionItems: [
          'Attend executive AI strategy workshop',
          'Engage with peer executives on AI transformation journeys',
          'Develop AI literacy across leadership team',
          'Establish regular AI progress reviews with board'
        ]
      });
    } else if (selectedRole === 'it-leader') {
      recommendations.push({
        title: 'Technical Architecture Review',
        description: 'As an IT leader, ensuring your technical foundation supports AI workloads is critical.',
        priority: 'medium',
        category: 'Technical Leadership',
        actionItems: [
          'Conduct AI readiness assessment of current infrastructure',
          'Evaluate AI platform and tooling options',
          'Design scalable MLOps and AI operations framework',
          'Establish technical standards for AI development'
        ]
      });
    } else if (selectedRole === 'compliance') {
      recommendations.push({
        title: 'AI Compliance Framework Development',
        description: 'As a compliance officer, establishing robust AI governance and risk management is your priority.',
        priority: 'medium',
        category: 'Compliance Leadership',
        actionItems: [
          'Develop AI-specific risk assessment methodology',
          'Create compliance monitoring and reporting dashboard',
          'Establish AI vendor due diligence process',
          'Design AI audit trail and documentation requirements'
        ]
      });
    }

    // Sort by priority
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    return recommendations.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);
  };

  const recommendations = generateRecommendations();

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'bg-destructive/10 text-destructive border-destructive/20';
      case 'medium':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'low':
        return 'bg-primary/10 text-primary border-primary/20';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'ExclamationTriangleIcon';
      case 'medium':
        return 'LightBulbIcon';
      case 'low':
        return 'InformationCircleIcon';
      default:
        return 'InformationCircleIcon';
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-2xl font-display font-bold text-secondary mb-2">
          Personalized Recommendations
        </h3>
        <p className="text-muted-foreground">
          Based on your responses, here are tailored action items to accelerate your AI readiness
        </p>
      </div>

      {recommendations.length === 0 ? (
        <div className="bg-accent/10 rounded-lg p-8 text-center">
          <Icon name="CheckCircleIcon" size={48} className="text-accent mx-auto mb-4" />
          <h4 className="text-xl font-display font-semibold text-secondary mb-2">
            Excellent AI Readiness!
          </h4>
          <p className="text-muted-foreground">
            Your organization demonstrates strong readiness across all dimensions. Continue monitoring and optimizing your AI initiatives.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {recommendations.map((rec, index) => (
            <div
              key={index}
              className="bg-card rounded-lg p-6 shadow-subtle border border-border hover:shadow-md transition-shadow duration-300"
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${getPriorityColor(rec.priority)}`}>
                    <Icon name={getPriorityIcon(rec.priority)} size={24} />
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h4 className="text-lg font-display font-semibold text-secondary mb-1">
                        {rec.title}
                      </h4>
                      <span className="text-xs font-medium text-muted-foreground">
                        {rec.category}
                      </span>
                    </div>
                    <span
                      className={`text-xs font-semibold px-3 py-1 rounded-full border ${getPriorityColor(rec.priority)}`}
                    >
                      {rec.priority.toUpperCase()} PRIORITY
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">
                    {rec.description}
                  </p>
                  <div className="space-y-2">
                    <h5 className="text-sm font-semibold text-secondary">Recommended Actions:</h5>
                    <ul className="space-y-2">
                      {rec.actionItems.map((action, actionIndex) => (
                        <li key={actionIndex} className="flex items-start gap-2 text-sm text-foreground">
                          <Icon name="CheckIcon" size={16} className="text-accent flex-shrink-0 mt-0.5" />
                          <span>{action}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-gradient-to-br from-primary/10 to-accent/10 rounded-lg p-6 border border-primary/20">
        <div className="flex items-start gap-4">
          <Icon name="SparklesIcon" size={24} className="text-primary flex-shrink-0 mt-1" />
          <div>
            <h4 className="text-lg font-display font-semibold text-secondary mb-2">
              Ready to Implement These Recommendations?
            </h4>
            <p className="text-sm text-muted-foreground mb-4">
              Schedule a consultation with Mindshift AI to develop a customized implementation roadmap based on these insights. Our experts will help you prioritize actions and accelerate your AI journey.
            </p>
            <button
              onClick={() => window.location.href = '/partnership-inquiry'}
              className="inline-flex items-center px-6 py-2 bg-primary text-white font-display font-semibold rounded-lg hover:shadow-authority transition-all duration-300 hover:-translate-y-0.5"
            >
              Schedule Expert Consultation
              <Icon name="ArrowRightIcon" size={16} className="ml-2" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContextualRecommendations;